import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from "@remix-run/react"
import { captureRemixErrorBoundaryError } from "@sentry/remix"
import { getErrorMessage } from "#app/utils/misc.tsx"

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => JSX.Element | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<ErrorDisplay 
			title={`${error.status} Error`}
			message={error.data}
		/>
	),
	statusHandlers,
	unexpectedErrorHandler = error => (
		<ErrorDisplay 
			title="Unexpected Error"
			message={getErrorMessage(error)}
		/>
	),
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => JSX.Element | null
}) {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)
	const params = useParams()

	if (typeof document !== "undefined") {
		console.error(error)
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-50">
			{isRouteErrorResponse(error)
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	)
}

function ErrorDisplay({ title, message }: { title: string; message: string }) {
	return (
		<div className="flex flex-col items-center justify-center px-8 text-center">
			<img
				src="/img/scl_logo.png"
				alt="Storycraft Lab"
				className="mb-8 h-16 w-auto"
			/>
			<div className="rounded-lg bg-white p-8 shadow-lg">
				<h1 className="mb-2 font-serif text-2xl font-medium text-gray-900">
					{title}
				</h1>
				<p className="text-lg text-gray-600">
					{message}
				</p>
				<p className="mt-6 text-sm text-gray-500">
					Please try refreshing the page or contact support if the problem persists.
				</p>
			</div>
		</div>
	)
}
